import React from 'react';
import { Image as ImageType, ImageState } from '../../../../types/api/ImageType';
import { RESPONSIVE_TYPE } from '../Responsive';
import { Picture } from './Picture';
import { Img } from './Img';
import { ImageWithoutAvailableWidths } from '../useImage';
import { ImageModifier } from '../Image.modifier';
import { ResponsiveType } from '../Image';

type ImgOrPicture = {
  image: ImageType;
  modifier?: ImageModifier;
  lazyLoading?: boolean;
  responsiveType?: ResponsiveType;
  getAlt: () => string;
  getWidthWithQueryParameter: (maxSize?: number) => number | undefined;
  getAvailableWidthOfImage: (imageObj: ImageWithoutAvailableWidths) => number[];
};

/**
 * Not all images are responsive and therefore exist in different widths. Some are just basic images:
 * - Default Images (for previews or in blogs and profiles)
 * - Only one width available for the image in the backend (portal avatar, portal logo)
 * -> Exception: imageGalleryImages! Here we generate a picture element
 *    for images that only have one width in availableWidths, although they are not responsive here.
 *    (ON2 image gallery migration generates too small images), see SON-12010 as well
 */
export function ImgOrPicture({
  image,
  modifier,
  lazyLoading,
  responsiveType,
  getAlt,
  getWidthWithQueryParameter,
  getAvailableWidthOfImage,
}: ImgOrPicture) {
  if (
    responsiveType &&
    image?.imageState &&
    image.imageState !== ImageState.defaultImage &&
    (getAvailableWidthOfImage(image as ImageWithoutAvailableWidths)?.length > 1 ||
      image.imageType === RESPONSIVE_TYPE.imageGalleryImage)
  ) {
    return (
      <Picture
        image={image}
        getAlt={getAlt}
        modifier={modifier}
        lazyLoading={lazyLoading}
        responsiveType={responsiveType}
        getWidthWithQueryParameter={getWidthWithQueryParameter}
        getAvailableWidthOfImage={getAvailableWidthOfImage}
      />
    );
  }

  return (
    <Img
      image={image}
      getAlt={getAlt}
      modifier={modifier}
      lazyLoading={lazyLoading}
      getWidthWithQueryParameter={getWidthWithQueryParameter}
    />
  );
}
